import { authenticatedFetch } from "./http";

export type OutboundOrderStatus =
  "SUBMITTED" | "ATP_AVAILABLE" | "ATP_FAILED" |
  "PARTIALLY_ALLOCATED" | "ALLOCATED" | "CANCELLED";

export interface OutboundOrderLine {
  id: string;
  lineNumber: number;
  itemId: string;
  itemCode: string;
  itemName: string;
  requestedQuantity: number;
  uom: string;
  allocationId: string | null;
  atpQuantity: number;
  atpCheckedAt: string | null;
  atpChecks: string[];
  expectedInboundQuantity: number;
  nextExpectedArrivalAt: string | null;
  status: "SUBMITTED" | "ATP_AVAILABLE" | "ATP_FAILED" |
    "ALLOCATED" | "CANCELLED";
  version: number;
}

export interface OutboundOrder {
  id: string;
  orderNumber: string;
  externalReference: string | null;
  sourceSystem: string;
  orderType: "CUSTOMER_ORDER" | "POU_REQUEST" |
    "STOCK_TRANSFER" | "EMERGENCY";
  warehouseId: string;
  warehouseCode: string;
  ownerClientId: string | null;
  ownerClientCode: string | null;
  recipientClientId: string;
  recipientClientCode: string;
  recipientClientName: string;
  destinationId: string;
  destinationCode: string;
  destinationName: string;
  destinationType: "POSTAL_ADDRESS" | "POU";
  requestedDeliveryFrom: string | null;
  requestedDeliveryTo: string | null;
  priority: number;
  routeReference: string | null;
  instructions: string | null;
  status: OutboundOrderStatus;
  submittedAt: string;
  acceptedAt: string | null;
  createdByDisplayName: string | null;
  version: number;
  lines: OutboundOrderLine[];
  milestones: Array<{
    code: string;
    label: string;
    state: "COMPLETED" | "CURRENT" | "PENDING" | "ERROR" | "CANCELLED";
    occurredAt: string | null;
    detailCode: "REGISTERED" | "STOCK_UNAVAILABLE" | "CHECK_REQUIRED" |
      "STOCK_AVAILABLE" | "ALLOCATED_LINES" | "PREPARATION_COMPLETED" |
      "PICKING_IN_PROGRESS" | "PREPARATION_PLANNED" | "DELIVERED" |
      "DELIVERY_IN_PROGRESS" | "MISSION_PLANNED" | "UPCOMING";
    detail: string;
    references: Array<{
      type: "OUTBOUND_ORDER" | "ALLOCATION" | "ALLOCATION_GROUP" |
        "PREPARATION_TASK" | "PREPARATION_WAVE" | "DELIVERY_MISSION";
      id: string;
      label: string;
    }>;
  }>;
}

export interface CreateOutboundOrder {
  warehouseId: string;
  ownerClientId: string | null;
  recipientClientId: string;
  destinationId: string;
  externalReference: string | null;
  orderType: OutboundOrder["orderType"];
  requestedDeliveryFrom: string | null;
  requestedDeliveryTo: string | null;
  priority: number;
  routeReference: string | null;
  instructions: string | null;
  lines: Array<{ itemId: string; quantity: number; uom: string }>;
}

export function loadOutboundOrders(): Promise<OutboundOrder[]> {
  return authenticatedFetch<OutboundOrder[]>("/api/v1/outbound-orders");
}

export function loadPortalOutboundOrders(): Promise<OutboundOrder[]> {
  return authenticatedFetch<OutboundOrder[]>(
    "/api/v1/portal/outbound-orders"
  );
}

export interface PortalOutboundCatalog {
  warehouses: Array<{
    id: string;
    code: string;
    displayName: string;
  }>;
  clients: Array<{
    id: string;
    code: string;
    displayName: string;
    destinations: Array<{
      id: string;
      code: string;
      displayName: string;
      type: "POSTAL_ADDRESS" | "POU";
    }>;
  }>;
  items: Array<{
    id: string;
    code: string;
    displayName: string;
    baseUom: string;
  }>;
}

export function loadPortalOutboundCatalog(): Promise<PortalOutboundCatalog> {
  return authenticatedFetch<PortalOutboundCatalog>(
    "/api/v1/portal/outbound-orders/catalog"
  );
}

export function createPortalOutboundOrder(
  request: CreateOutboundOrder
): Promise<OutboundOrder> {
  return authenticatedFetch<OutboundOrder>(
    "/api/v1/portal/outbound-orders",
    { method: "POST", body: JSON.stringify(request) }
  );
}

export type PortalAsnScenario =
  "STANDARD" | "MULTI_LINE" | "LOT" | "SERIALIZED" | "CUSTOMS";

export interface PortalAsnSimulation {
  id: string;
  asnNumber: string;
  scenario: PortalAsnScenario;
  warehouseCode: string;
  supplierCode: string;
  supplierName: string;
  expectedArrivalAt: string;
  status: string;
  lineCount: number;
  customsClearanceRequired: boolean;
  customsClearanceNumber: string | null;
  customsClearanceStatus: string | null;
  createdAt: string;
}

export function loadPortalAsnSimulations(): Promise<PortalAsnSimulation[]> {
  return authenticatedFetch<PortalAsnSimulation[]>(
    "/api/v1/portal/asn-simulations"
  );
}

export function createPortalAsnSimulation(
  request: { scenario: PortalAsnScenario; customsClearanceRequired: boolean }
): Promise<PortalAsnSimulation> {
  return authenticatedFetch<PortalAsnSimulation>(
    "/api/v1/portal/asn-simulations",
    { method: "POST", body: JSON.stringify(request) }
  );
}

export function acceptOutboundOrder(
  order: OutboundOrder
): Promise<OutboundOrder> {
  return authenticatedFetch<OutboundOrder>(
    `/api/v1/outbound-orders/${order.id}/accept`,
    { method: "POST", body: JSON.stringify({ version: order.version }) }
  );
}

export function allocateOutboundOrderLine(
  order: OutboundOrder, line: OutboundOrderLine
): Promise<OutboundOrder> {
  return authenticatedFetch<OutboundOrder>(
    `/api/v1/outbound-orders/${order.id}/lines/${line.id}/allocate`,
    {
      method: "POST",
      body: JSON.stringify({ version: line.version })
    }
  );
}

export function cancelPortalOutboundOrder(
  order: OutboundOrder, reason: string
): Promise<OutboundOrder> {
  return authenticatedFetch<OutboundOrder>(
    `/api/v1/portal/outbound-orders/${order.id}/cancel`,
    {
      method: "POST",
      body: JSON.stringify({ version: order.version, reason })
    }
  );
}
